import { find, orderBy, sortBy } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { FILE_UPLOAD_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { STANDARD_POPULATION_TYPES } from '@/constants/standard_populations';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import {
  ALL_SEGMENTS,
  PartnerPopulation,
  Population,
  PopulationStats,
  STANDARD_POPULATIONS,
  SegmentType,
  StandardPopulationType,
} from '@/types/populations';

export const usePopulationsStore = defineStore('Populations', () => {
  const populations = ref<Population[]>([]);
  const partnerPopulations = ref<PartnerPopulation[]>([]);
  const inactivePopulations = ref<Population[]>([]);
  const populationStats = ref<PopulationStats[]>([]);
  const uniqueRecordCount = ref<number | null>(null);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const [
        populationResponse,
        inactivePopsResponse,
        populationStatsResponse,
        partnerPopResponse,
        recordStatsResponse,
      ] = await Promise.all([
        crossbeamApi.GET('/v0.3/populations', {
          params: { query: { only_inactive: 'false' } },
        }),
        crossbeamApi.GET('/v0.3/populations', {
          params: { query: { only_inactive: 'true' } },
        }),
        crossbeamApi.GET('/alpha/population-stats'),
        crossbeamApi.GET('/v0.1/partner-populations'),
        crossbeamApi.GET('/v0.1/population-record-stats'),
      ]);
      const err =
        populationResponse.error ||
        inactivePopsResponse.error ||
        populationStatsResponse.error ||
        partnerPopResponse.error ||
        recordStatsResponse.error;

      if (err) {
        captureException(err);
        return;
      }
      populations.value = sortAndAddMetadata({
        populations: populationResponse.data?.items,
        populationStats: populationStatsResponse.data?.items,
      });
      inactivePopulations.value = sortAndAddMetadata({
        populations: inactivePopsResponse.data?.items,
        populationStats: populationStatsResponse.data?.items,
      });
      populationStats.value = populationStatsResponse.data?.items ?? [];
      partnerPopulations.value = partnerPopResponse.data?.items ?? [];
      uniqueRecordCount.value = recordStatsResponse.data?.unique_record_count;
    },
  );

  refresh({ useCache: true });

  const customPopulations = computed(() => {
    return populations.value.filter((pop) => {
      if (!pop.standard_type) return true;
      return !STANDARD_POPULATION_TYPES.includes(pop.standard_type);
    });
  });

  const standardPopulations = computed(() => {
    return populations.value.filter((pop) => {
      return (
        pop.standard_type &&
        STANDARD_POPULATION_TYPES.includes(pop.standard_type)
      );
    });
  });

  const segmentPopulations = computed(() =>
    populations.value.filter((pop) =>
      ALL_SEGMENTS.includes(pop.segment as SegmentType),
    ),
  );

  const missingStandardPopulations = computed(() =>
    STANDARD_POPULATION_TYPES.filter(
      (type) =>
        !standardPopulations.value.some((pop) => pop.standard_type === type),
    ),
  );

  const populationIds = computed(() => populations.value.map((pop) => pop.id));

  const fileUploadPopulations = computed(() =>
    populations.value.filter((pop) =>
      pop.base_schema.includes(FILE_UPLOAD_DATA_SOURCE_TYPE),
    ),
  );

  const sortedPartnerPopulations = computed(() =>
    sortBy(partnerPopulations.value, [
      // 1. Primary Sort: Put standard pops at front in order defined by STANDARD_POPULATIONS
      (pop) => {
        if (!pop.standard_type) return 999;
        const index = STANDARD_POPULATIONS.indexOf(pop.standard_type);
        return index === -1 ? 999 : index;
      },
      // 2. Secondary Sort: Automatically sorts remaining items alphabetically by 'name'
      'name',
    ]),
  );

  function getPopulationById(id: number) {
    return populations.value.find((pop) => pop.id === id);
  }

  function getPopulationByName(name: string) {
    return populations.value.find(
      (pop) => pop.name?.toLowerCase() === name?.toLowerCase(),
    );
  }

  function getPartnerPopulationById(id: number) {
    return partnerPopulations.value.find((pop) => pop.id === id);
  }

  function getPopulationsBySegment(segment: SegmentType) {
    return populations.value.filter((pop) => pop.segment === segment);
  }

  function getPartnerPopulationsByOrg(partnerOrgId: number) {
    return partnerPopulations.value.filter(
      (pop) => pop.organization_id === partnerOrgId,
    );
  }

  function getPopulationByStandardType(type: StandardPopulationType) {
    return populations.value.find((pop) => pop.standard_type === type);
  }

  function getPartnerPopulationsByStandardType(type: StandardPopulationType) {
    return partnerPopulations.value.filter(
      (population) => population.standard_type === type,
    );
  }

  function getPartnerPopulationsBySegment(segment: SegmentType) {
    return sortedPartnerPopulations.value.filter(
      (population) => population.segment === segment,
    );
  }

  function getPartnerPopulationsBySegmentAndPartner(
    segment: SegmentType,
    organizationId: number,
  ) {
    return sortedPartnerPopulations.value.filter(
      (population) =>
        population.segment === segment &&
        population.organization_id === organizationId,
    );
  }

  // Selectively refresh just partner populations
  async function refreshPartnerPopulations() {
    const { data, error } = await crossbeamApi.GET('/v0.1/partner-populations');
    if (error) {
      captureException(error);
      return;
    }
    partnerPopulations.value = data.items;
  }

  function upsertPopulation(population: Population) {
    populations.value = populations.value
      .filter((s) => s.id !== population.id)
      .concat(population);
  }

  function removePopulation(id: number) {
    populations.value = populations.value.filter((s) => s.id !== id);
  }

  function setPopulationCount(populationId: number, count: number) {
    const pop = populations.value.find((p) => p.id === populationId);
    if (pop) {
      (pop as Population & { meta?: PopulationStats }).meta = {
        ...((pop as Population & { meta?: PopulationStats }).meta || {}),
        population_id: populationId,
        total_size: count,
      } as PopulationStats;
    }
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    refreshPopulationsStore: refresh,
    refreshPartnerPopulations,
    populations,
    customPopulations,
    standardPopulations,
    segmentPopulations,
    sortedPartnerPopulations,
    uniqueRecordCount,
    partnerPopulations,
    inactivePopulations,
    populationStats,
    populationIds,
    fileUploadPopulations,
    getPopulationById,
    getPopulationByName,
    getPopulationsBySegment,
    getPartnerPopulationById,
    getPartnerPopulationsByOrg,
    getPopulationByStandardType,
    getPartnerPopulationsByStandardType,
    getPartnerPopulationsBySegment,
    getPartnerPopulationsBySegmentAndPartner,
    missingStandardPopulations,
    upsertPopulation,
    removePopulation,
    setPopulationCount,
  };
});

export function sortAndAddMetadata({
  populations,
  populationStats,
}: {
  populations?: Population[];
  populationStats?: PopulationStats[];
}) {
  return orderBy(
    populations?.map((pop) => ({
      ...pop,
      meta: find(populationStats, (s) => s.population_id === pop.id) || {},
    })) as (Population & { meta: PopulationStats })[],
    ['name'],
    ['asc'],
  );
}
